/**
 * PARTNER-NET-01: CRM Partner Directory
 * 
 * Canonical Partner Master — reads and writes the suppliers collection.
 * Features: list, search, filter, sort, create, edit, approve/suspend/archive,
 *           capability tags, certifications, geo coverage, CRM account linkage,
 *           category map, assignment dry-run.
 * Desktop + mobile responsive.
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { partnerNetApi } from "../services/api";
import { toast } from "./ui/sonner";
import {
  Search, Plus, Filter, ChevronDown, ChevronUp, X, Edit2,
  CheckCircle, AlertCircle, Archive, Shield, Tag, MapPin,
  Link2, ExternalLink, RefreshCw, FileText, Users, Star,
  Building2, Phone, Mail, Globe2, Award, Layers, Briefcase,
  MoreVertical,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";

// ── Constants ──────────────────────────────────────────────────────────────────
const PARTNER_TYPES = [
  { value: "VENDOR",            label: "Vendor",            color: "bg-slate-100 text-slate-700" },
  { value: "SUBCONTRACTOR",     label: "Subcontractor",     color: "bg-amber-50 text-amber-800" },
  { value: "PREFERRED_PARTNER", label: "Preferred Partner", color: "bg-emerald-50 text-emerald-800" },
  { value: "STRATEGIC",         label: "Strategic",         color: "bg-violet-50 text-violet-800" },
];
const STATUSES = [
  { value: "APPROVED",  label: "Approved",  color: "bg-emerald-100 text-emerald-800" },
  { value: "DRAFT",     label: "Draft",     color: "bg-slate-100 text-slate-600" },
  { value: "SUSPENDED", label: "Suspended", color: "bg-amber-100 text-amber-800" },
  { value: "ARCHIVED",  label: "Archived",  color: "bg-red-50 text-red-700" },
];
const CERT_TYPES = ["ISO_9001","FSSAI","FIRE_SAFETY","GST_REGISTERED","MSME","OTHER"];

const INDIA_STATES = ["Andhra Pradesh","Assam","Bihar","Delhi","Goa","Gujarat","Haryana",
  "Karnataka","Kerala","Madhya Pradesh","Maharashtra","Punjab","Rajasthan","Tamil Nadu",
  "Telangana","Uttar Pradesh","West Bengal","Uttarakhand","Other"];

const statusBadge = (status) => {
  const s = STATUSES.find(x => x.value === status) || STATUSES[1];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
};
const partnerTypeBadge = (type) => {
  if (!type) return null;
  const t = PARTNER_TYPES.find(x => x.value === type);
  if (!t) return null;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${t.color}`}>{t.label}</span>;
};

// ── Main Component ─────────────────────────────────────────────────────────────
export default function PartnerDirectory() {
  const [partners, setPartners]     = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters]       = useState({ status: "", partner_type: "", service_category_id: "", city: "", search: "" });
  const [sort, setSort]             = useState("company_name");
  const [skip, setSkip]             = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected]     = useState(null);       // partner detail panel
  const [showForm, setShowForm]     = useState(false);      // create/edit form
  const [editPartner, setEditPartner] = useState(null);     // null = create, obj = edit
  const [showDryRun, setShowDryRun] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [catMap, setCatMap]         = useState(null);
  const LIMIT = 20;

  // Load category map once
  useEffect(() => {
    partnerNetApi.getCategoryMap()
      .then(r => {
        setCatMap(r.data);
        setCategories(r.data?.entries?.filter(e => e.ueim_id) || []);
      })
      .catch(() => {});
  }, []);

  const loadPartners = useCallback(async (reset = false) => {
    setLoading(true);
    const offset = reset ? 0 : skip;
    if (reset) setSkip(0);
    try {
      const params = { limit: LIMIT, skip: offset, sort, ...Object.fromEntries(Object.entries(filters).filter(([,v]) => v)) };
      const r = await partnerNetApi.listPartners(params);
      setPartners(r.data.results || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Failed to load partners");
    } finally {
      setLoading(false);
    }
  }, [filters, sort, skip]);

  useEffect(() => { loadPartners(true); }, [filters, sort]); // eslint-disable-line
  useEffect(() => { loadPartners(); }, [skip]); // eslint-disable-line

  const openEdit = (p) => { setEditPartner(p); setShowForm(true); };
  const openCreate = () => { setEditPartner(null); setShowForm(true); };

  const handleRunDryRun = async () => {
    setDryRunLoading(true);
    try {
      const r = await partnerNetApi.runAssignmentDryRun();
      const report = r.data?.report || r.data;
      setDryRunResult(report);
      setShowDryRun(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally {
      setDryRunLoading(false);
    }
  };

  return (
    <div data-testid="partner-directory" className="min-h-screen bg-[#FAF8F5] font-sans">
      {/* Header */}
      <div className="bg-white border-b border-[#E8E0D0] px-4 sm:px-8 py-5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-serif font-semibold text-[#1C1C1E]">Partner Directory</h1>
            <p className="text-sm text-[#6B6B6B] mt-0.5">
              {total} partner{total !== 1 ? "s" : ""} · Canonical Partner Master
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="run-dry-run-btn"
              variant="outline"
              size="sm"
              onClick={handleRunDryRun}
              disabled={dryRunLoading}
              className="border-[#C8B89A] text-[#6B5E4A] hover:bg-[#F5EFE5]"
            >
              <FileText className="h-3.5 w-3.5 mr-1.5" />
              {dryRunLoading ? "Running…" : "Assignment Dry-Run"}
            </Button>
            <Button
              data-testid="create-partner-btn"
              onClick={openCreate}
              className="bg-[#B8965A] hover:bg-[#A07840] text-white"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Partner
            </Button>
          </div>
        </div>
      </div>

      {/* Search + Filter Bar */}
      <div className="bg-white border-b border-[#E8E0D0] px-4 sm:px-8 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9B8B7A]" />
            <Input
              data-testid="partner-search-input"
              placeholder="Search name, contact, tag…"
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              className="pl-9 border-[#D4C8B8] bg-[#FAFAF8] focus:border-[#B8965A] text-sm"
            />
            {filters.search && (
              <button onClick={() => setFilters(f => ({ ...f, search: "" }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9B8B7A] hover:text-[#1C1C1E]">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <Button
            data-testid="filter-toggle-btn"
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(f => !f)}
            className={`border-[#D4C8B8] text-[#6B6B6B] ${showFilters ? "bg-[#F5EFE5] border-[#B8965A] text-[#6B5E4A]" : ""}`}
          >
            <Filter className="h-3.5 w-3.5 mr-1.5" />
            Filters
            {showFilters ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
          </Button>
          <select
            data-testid="sort-select"
            value={sort}
            onChange={e => setSort(e.target.value)}
            className="text-sm border border-[#D4C8B8] rounded-md px-2 py-1.5 bg-white text-[#3D3D3D] focus:outline-none focus:border-[#B8965A]"
          >
            <option value="company_name">Name A–Z</option>
            <option value="created_at">Newest</option>
            <option value="status">Status</option>
          </select>
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="max-w-7xl mx-auto mt-3 flex flex-wrap gap-2" data-testid="filter-panel">
            <FilterSelect label="Status" value={filters.status} options={[{ value: "", label: "All statuses" }, ...STATUSES]} onChange={v => setFilters(f => ({ ...f, status: v }))} testId="filter-status" />
            <FilterSelect label="Type" value={filters.partner_type} options={[{ value: "", label: "All types" }, ...PARTNER_TYPES]} onChange={v => setFilters(f => ({ ...f, partner_type: v }))} testId="filter-type" />
            <FilterSelect label="Category" value={filters.service_category_id} options={[{ value: "", label: "All categories" }, ...categories.map(c => ({ value: c.ueim_id, label: c.canonical_name }))]} onChange={v => setFilters(f => ({ ...f, service_category_id: v }))} testId="filter-category" />
            <CityInput value={filters.city} onChange={v => setFilters(f => ({ ...f, city: v }))} testId="filter-city" />
            {Object.values(filters).some(Boolean) && (
              <button onClick={() => setFilters({ status: "", partner_type: "", service_category_id: "", city: "", search: "" })} className="text-xs text-[#B8965A] underline self-center">Clear all</button>
            )}
          </div>
        )}
      </div>

      {/* Partner Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6">
        {loading ? (
          <div className="flex justify-center items-center py-24">
            <div className="animate-spin h-8 w-8 border-2 border-[#B8965A] border-t-transparent rounded-full" />
          </div>
        ) : partners.length === 0 ? (
          <EmptyState onAdd={openCreate} />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="partner-grid">
              {partners.map(p => (
                <PartnerCard
                  key={p.id}
                  partner={p}
                  onView={() => setSelected(p.id)}
                  onEdit={() => openEdit(p)}
                />
              ))}
            </div>
            {/* Pagination */}
            {total > LIMIT && (
              <div className="flex justify-center items-center gap-4 mt-8">
                <button disabled={skip === 0} onClick={() => setSkip(s => Math.max(0, s - LIMIT))} className="text-sm text-[#6B5E4A] disabled:text-[#C0B8AC] hover:text-[#B8965A]">← Previous</button>
                <span className="text-sm text-[#6B6B6B]">{Math.floor(skip/LIMIT)+1} / {Math.ceil(total/LIMIT)}</span>
                <button disabled={skip + LIMIT >= total} onClick={() => setSkip(s => s + LIMIT)} className="text-sm text-[#6B5E4A] disabled:text-[#C0B8AC] hover:text-[#B8965A]">Next →</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Partner Detail Panel */}
      {selected && (
        <PartnerDetailPanel
          partnerId={selected}
          onClose={() => setSelected(null)}
          onEdit={(p) => { openEdit(p); setSelected(null); }}
          onRefresh={loadPartners}
        />
      )}

      {/* Create / Edit Form */}
      {showForm && (
        <PartnerForm
          partner={editPartner}
          categories={categories}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); loadPartners(true); }}
        />
      )}

      {/* Dry-Run Modal */}
      {showDryRun && dryRunResult && (
        <DryRunModal result={dryRunResult} onClose={() => setShowDryRun(false)} />
      )}
    </div>
  );
}

// ── Partner Card ───────────────────────────────────────────────────────────────
function PartnerCard({ partner: p, onView, onEdit }) {
  return (
    <div
      data-testid={`partner-card-${p.id}`}
      className="bg-white rounded-xl border border-[#E8E0D0] p-4 hover:border-[#B8965A] hover:shadow-md transition-all cursor-pointer group"
      onClick={onView}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="w-9 h-9 rounded-lg bg-[#F5EFE5] flex items-center justify-center flex-shrink-0">
          <Building2 className="h-4 w-4 text-[#B8965A]" />
        </div>
        <div className="flex items-center gap-1.5">
          {statusBadge(p.status)}
          <button
            data-testid={`partner-edit-btn-${p.id}`}
            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#F5EFE5] transition-opacity"
            onClick={e => { e.stopPropagation(); onEdit(); }}
          >
            <Edit2 className="h-3.5 w-3.5 text-[#9B8B7A]" />
          </button>
        </div>
      </div>
      <h3 className="font-semibold text-[#1C1C1E] text-sm leading-tight mt-2 line-clamp-2">{p.display_name || p.company_name}</h3>
      {p.primary_contact_name && (
        <p className="text-xs text-[#6B6B6B] mt-1">{p.primary_contact_name}</p>
      )}
      <div className="mt-2.5 flex flex-wrap gap-1">
        {p.partner_type && partnerTypeBadge(p.partner_type)}
        {(p.service_categories || []).slice(0, 2).map(c => (
          <span key={c.id} className="text-xs bg-[#F5EFE5] text-[#6B5E4A] px-1.5 py-0.5 rounded">
            {c.name}
          </span>
        ))}
        {(p.service_categories || []).length > 2 && (
          <span className="text-xs text-[#9B8B7A]">+{(p.service_categories||[]).length - 2}</span>
        )}
      </div>
      {(p.city || p.crm_account_id) && (
        <div className="mt-2 flex items-center gap-3 text-xs text-[#9B8B7A]">
          {p.city && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{p.city}</span>}
          {p.crm_account_id && <span className="flex items-center gap-0.5 text-[#B8965A]"><Link2 className="h-3 w-3" />CRM</span>}
        </div>
      )}
    </div>
  );
}

// ── Partner Detail Panel (slide-over) ─────────────────────────────────────────
function PartnerDetailPanel({ partnerId, onClose, onEdit, onRefresh }) {
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction]   = useState(null);

  useEffect(() => {
    setLoading(true);
    partnerNetApi.getPartner(partnerId)
      .then(r => setPartner(r.data))
      .catch(() => toast.error("Failed to load partner"))
      .finally(() => setLoading(false));
  }, [partnerId]);

  const doStatusAction = async (fn, label) => {
    try {
      setAction(label);
      await fn;
      toast.success(`Partner ${label.toLowerCase()}`);
      onRefresh();
      const r = await partnerNetApi.getPartner(partnerId);
      setPartner(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed`);
    } finally { setAction(null); }
  };

  const doUnlinkCRM = async () => {
    try {
      await partnerNetApi.unlinkCRMAccount(partnerId);
      toast.success("CRM account unlinked");
      const r = await partnerNetApi.getPartner(partnerId);
      setPartner(r.data);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlink failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="partner-detail-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative ml-auto w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[#E8E0D0] px-6 py-4 flex items-center justify-between z-10">
          <h2 className="font-serif text-lg font-semibold text-[#1C1C1E]">Partner Profile</h2>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => onEdit(partner)} className="border-[#C8B89A] text-[#6B5E4A]">
              <Edit2 className="h-3.5 w-3.5 mr-1" />Edit
            </Button>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-[#F5EFE5]">
              <X className="h-5 w-5 text-[#6B6B6B]" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex-1 flex justify-center items-center">
            <div className="animate-spin h-8 w-8 border-2 border-[#B8965A] border-t-transparent rounded-full" />
          </div>
        ) : partner ? (
          <div className="flex-1 overflow-y-auto">
            {/* Identity */}
            <div className="px-6 py-5 border-b border-[#F0E8DC]">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#F5EFE5] flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-6 w-6 text-[#B8965A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-[#1C1C1E] text-lg leading-tight">{partner.display_name || partner.company_name}</h3>
                  {partner.display_name && partner.display_name !== partner.company_name && (
                    <p className="text-sm text-[#6B6B6B] mt-0.5">{partner.company_name}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {statusBadge(partner.status)}
                    {partnerTypeBadge(partner.partner_type)}
                  </div>
                </div>
              </div>

              {/* Status Actions */}
              <div className="flex gap-2 mt-4">
                {partner.status !== "APPROVED" && partner.status !== "ARCHIVED" && (
                  <button data-testid="approve-partner-btn" onClick={() => doStatusAction(partnerNetApi.approvePartner(partner.id), "Approved")} disabled={!!action} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 font-medium">
                    {action === "Approved" ? "…" : <><CheckCircle className="h-3 w-3 inline mr-1" />Approve</>}
                  </button>
                )}
                {partner.status === "APPROVED" && (
                  <button data-testid="suspend-partner-btn" onClick={() => doStatusAction(partnerNetApi.suspendPartner(partner.id), "Suspended")} disabled={!!action} className="text-xs px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 font-medium">
                    {action === "Suspended" ? "…" : <><AlertCircle className="h-3 w-3 inline mr-1" />Suspend</>}
                  </button>
                )}
                {partner.status !== "ARCHIVED" && (
                  <button data-testid="archive-partner-btn" onClick={() => doStatusAction(partnerNetApi.archivePartner(partner.id), "Archived")} disabled={!!action} className="text-xs px-3 py-1.5 rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 font-medium">
                    {action === "Archived" ? "…" : <><Archive className="h-3 w-3 inline mr-1" />Archive</>}
                  </button>
                )}
              </div>
            </div>

            {/* Contact */}
            <Section title="Contact" icon={<Users className="h-4 w-4" />}>
              <InfoRow label="Contact" value={partner.primary_contact_name} />
              <InfoRow label="Email" value={partner.email} />
              <InfoRow label="Phone" value={partner.phone} />
              <InfoRow label="City" value={partner.city} />
              <InfoRow label="GST Ref" value={partner.gst_ref} />
            </Section>

            {/* Categories */}
            {partner.service_categories?.length > 0 && (
              <Section title="Service Categories" icon={<Layers className="h-4 w-4" />}>
                <div className="flex flex-wrap gap-1.5">
                  {partner.service_categories.map(c => (
                    <span key={c.id} className="text-xs bg-[#F5EFE5] text-[#6B5E4A] border border-[#E8DCB8] px-2 py-1 rounded-lg">{c.name}</span>
                  ))}
                </div>
              </Section>
            )}

            {/* Capability Tags */}
            {partner.capability_tags?.length > 0 && (
              <Section title="Capabilities" icon={<Tag className="h-4 w-4" />}>
                <div className="flex flex-wrap gap-1.5">
                  {partner.capability_tags.map((t, i) => (
                    <span key={i} className="text-xs bg-blue-50 text-blue-700 border border-blue-100 px-2 py-1 rounded-lg">{t}</span>
                  ))}
                </div>
              </Section>
            )}

            {/* Geo Coverage */}
            {partner.geo_coverage && (partner.geo_coverage.pan_india || partner.geo_coverage.cities?.length > 0 || partner.geo_coverage.states?.length > 0) && (
              <Section title="Geographic Coverage" icon={<Globe2 className="h-4 w-4" />}>
                {partner.geo_coverage.pan_india && <div className="text-sm text-emerald-700 font-medium mb-2">Pan-India Coverage</div>}
                {partner.geo_coverage.states?.length > 0 && (
                  <InfoRow label="States" value={partner.geo_coverage.states.join(", ")} />
                )}
                {partner.geo_coverage.cities?.length > 0 && (
                  <InfoRow label="Cities" value={partner.geo_coverage.cities.join(", ")} />
                )}
              </Section>
            )}

            {/* Certifications */}
            {partner.certifications?.length > 0 && (
              <Section title="Certifications" icon={<Award className="h-4 w-4" />}>
                {partner.certifications.map((c, i) => (
                  <div key={i} className="flex items-start justify-between py-1.5 border-b border-[#F0E8DC] last:border-0">
                    <div>
                      <span className="text-sm font-medium text-[#1C1C1E]">{c.cert_type}</span>
                      {c.cert_number && <span className="text-xs text-[#6B6B6B] ml-2">#{c.cert_number}</span>}
                      {c.issued_by && <p className="text-xs text-[#9B8B7A] mt-0.5">Issued by: {c.issued_by}</p>}
                      {c.valid_until && <p className="text-xs text-[#9B8B7A]">Valid until: {c.valid_until}</p>}
                    </div>
                    {c.verified && <Shield className="h-4 w-4 text-emerald-600 flex-shrink-0" />}
                  </div>
                ))}
              </Section>
            )}

            {/* CRM Linkage */}
            <Section title="CRM Relationship" icon={<Link2 className="h-4 w-4" />}>
              {partner.crm_account ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#1C1C1E] font-medium">{partner.crm_account.name}</p>
                    <p className="text-xs text-[#9B8B7A] font-mono mt-0.5">{partner.crm_account.id}</p>
                  </div>
                  <button onClick={doUnlinkCRM} data-testid="unlink-crm-btn" className="text-xs text-red-600 hover:text-red-800 border border-red-200 px-2 py-1 rounded hover:bg-red-50">
                    Unlink
                  </button>
                </div>
              ) : (
                <p className="text-sm text-[#9B8B7A] italic">No CRM account linked. CRM linkage is optional.</p>
              )}
            </Section>

            {/* Sourcing Activity */}
            {partner.sourcing_summary && (
              <Section title="Sourcing Activity" icon={<Briefcase className="h-4 w-4" />}>
                <div className="grid grid-cols-3 gap-3">
                  <SourcingCount label="Invitations" value={partner.sourcing_summary.invitation_count} />
                  <SourcingCount label="Quotations" value={partner.sourcing_summary.quotation_count} />
                  <SourcingCount label="Selections" value={partner.sourcing_summary.selection_count} />
                </div>
              </Section>
            )}

            {partner.notes && (
              <Section title="Notes" icon={<FileText className="h-4 w-4" />}>
                <p className="text-sm text-[#3D3D3D] whitespace-pre-wrap">{partner.notes}</p>
              </Section>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── Partner Create / Edit Form ─────────────────────────────────────────────────
function PartnerForm({ partner, categories, onClose, onSaved }) {
  const isEdit = !!partner;
  const [tab, setTab]           = useState("basic");
  const [saving, setSaving]     = useState(false);
  const [crmSearch, setCrmSearch] = useState("");
  const [crmResults, setCrmResults] = useState([]);
  const [crmSearching, setCrmSearching] = useState(false);
  const crmTimerRef = useRef(null);

  const [form, setForm] = useState({
    company_name:         partner?.company_name || "",
    display_name:         partner?.display_name || "",
    primary_contact_name: partner?.primary_contact_name || "",
    email:                partner?.email || "",
    phone:                partner?.phone || "",
    city:                 partner?.city || "",
    gst_ref:              partner?.gst_ref || "",
    notes:                partner?.notes || "",
    service_category_ids: partner?.service_category_ids || [],
    partner_type:         partner?.partner_type || "",
    capability_tags:      (partner?.capability_tags || []).join(", "),
    geo_pan_india:        partner?.geo_coverage?.pan_india || false,
    geo_cities:           (partner?.geo_coverage?.cities || []).join(", "),
    geo_states:           partner?.geo_coverage?.states || [],
    certifications:       partner?.certifications || [],
    crm_account_id:       partner?.crm_account_id || "",
    crm_account_name:     partner?.crm_account?.name || "",
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const searchCRM = useCallback(async (q) => {
    if (!q || q.length < 2) { setCrmResults([]); return; }
    setCrmSearching(true);
    try {
      const { default: api } = await import("../services/api");
      const r = await api.api.get("/crm/search", { params: { q, limit: 5 } });
      const hits = (r.data.results || r.data).filter(x => x.entity_type === "ACCOUNT" || x.type === "customer" || !x.entity_type);
      setCrmResults(hits.slice(0, 5));
    } catch { setCrmResults([]); }
    finally { setCrmSearching(false); }
  }, []);

  const handleCrmInput = (v) => {
    setCrmSearch(v);
    clearTimeout(crmTimerRef.current);
    crmTimerRef.current = setTimeout(() => searchCRM(v), 300);
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    if (!form.primary_contact_name.trim()) { toast.error("Contact name is required"); return; }
    if (!form.email.trim()) { toast.error("Email is required"); return; }

    setSaving(true);
    try {
      const payload = {
        company_name:         form.company_name.trim(),
        display_name:         form.display_name.trim() || form.company_name.trim(),
        primary_contact_name: form.primary_contact_name.trim(),
        email:                form.email.trim(),
        phone:                form.phone.trim() || null,
        city:                 form.city.trim() || null,
        gst_ref:              form.gst_ref.trim() || null,
        notes:                form.notes.trim() || null,
        service_category_ids: form.service_category_ids,
        partner_type:         form.partner_type || null,
        capability_tags:      form.capability_tags.split(",").map(t => t.trim()).filter(Boolean),
        geo_coverage: {
          pan_india: form.geo_pan_india,
          cities:    form.geo_cities.split(",").map(c => c.trim()).filter(Boolean),
          states:    form.geo_states,
        },
        certifications: form.certifications,
        crm_account_id: form.crm_account_id || null,
      };

      if (isEdit) {
        await partnerNetApi.updatePartner(partner.id, payload);
        toast.success("Partner updated");
      } else {
        await partnerNetApi.createPartner(payload);
        toast.success("Partner created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (id) => {
    set("service_category_ids", form.service_category_ids.includes(id)
      ? form.service_category_ids.filter(x => x !== id)
      : [...form.service_category_ids, id]);
  };

  const addCert = () => set("certifications", [...form.certifications, { cert_type: "OTHER", cert_number: "", issued_by: "", valid_from: "", valid_until: "", verified: false }]);
  const removeCert = (i) => set("certifications", form.certifications.filter((_, idx) => idx !== i));
  const updateCert = (i, k, v) => {
    const certs = [...form.certifications];
    certs[i] = { ...certs[i], [k]: v };
    set("certifications", certs);
  };

  const TABS = [
    { id: "basic",  label: "Basic Info" },
    { id: "network",label: "Partner Network" },
    { id: "certs",  label: "Certifications" },
    { id: "crm",    label: "CRM Linkage" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="partner-form-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#E8E0D0] flex items-center justify-between flex-shrink-0">
          <h2 className="font-serif text-lg font-semibold text-[#1C1C1E]">
            {isEdit ? `Edit — ${partner.company_name}` : "New Partner"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#F5EFE5]"><X className="h-5 w-5 text-[#6B6B6B]" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#E8E0D0] px-6 flex-shrink-0">
          {TABS.map(t => (
            <button
              key={t.id}
              data-testid={`form-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-[#B8965A] text-[#6B5E4A]" : "border-transparent text-[#6B6B6B] hover:text-[#3D3D3D]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {tab === "basic" && (
            <div className="space-y-4">
              <FormRow label="Company Name *">
                <Input data-testid="field-company-name" value={form.company_name} onChange={e => set("company_name", e.target.value)} placeholder="e.g. Golden Arch Decor Pvt Ltd" className="border-[#D4C8B8] focus:border-[#B8965A]" />
              </FormRow>
              <FormRow label="Display Name">
                <Input data-testid="field-display-name" value={form.display_name} onChange={e => set("display_name", e.target.value)} placeholder="Short name for directory" className="border-[#D4C8B8] focus:border-[#B8965A]" />
              </FormRow>
              <div className="grid grid-cols-2 gap-4">
                <FormRow label="Contact Name *">
                  <Input data-testid="field-contact-name" value={form.primary_contact_name} onChange={e => set("primary_contact_name", e.target.value)} className="border-[#D4C8B8] focus:border-[#B8965A]" />
                </FormRow>
                <FormRow label="Email *">
                  <Input data-testid="field-email" type="email" value={form.email} onChange={e => set("email", e.target.value)} className="border-[#D4C8B8] focus:border-[#B8965A]" />
                </FormRow>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <FormRow label="Phone">
                  <Input data-testid="field-phone" value={form.phone} onChange={e => set("phone", e.target.value)} className="border-[#D4C8B8] focus:border-[#B8965A]" />
                </FormRow>
                <FormRow label="City">
                  <Input data-testid="field-city" value={form.city} onChange={e => set("city", e.target.value)} className="border-[#D4C8B8] focus:border-[#B8965A]" />
                </FormRow>
              </div>
              <FormRow label="GST Reference">
                <Input data-testid="field-gst-ref" value={form.gst_ref} onChange={e => set("gst_ref", e.target.value)} placeholder="e.g. 27AAAAA0000A1Z5" className="border-[#D4C8B8] focus:border-[#B8965A]" />
              </FormRow>
              <FormRow label="Service Categories">
                <div className="flex flex-wrap gap-2 mt-1">
                  {categories.map(c => (
                    <button
                      key={c.ueim_id}
                      data-testid={`cat-toggle-${c.ueim_id}`}
                      onClick={() => toggleCategory(c.ueim_id)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        form.service_category_ids.includes(c.ueim_id)
                          ? "bg-[#B8965A] text-white border-[#B8965A]"
                          : "bg-white text-[#6B5E4A] border-[#D4C8B8] hover:border-[#B8965A]"
                      }`}
                    >
                      {c.canonical_name}
                    </button>
                  ))}
                </div>
              </FormRow>
              <FormRow label="Notes">
                <textarea data-testid="field-notes" value={form.notes} onChange={e => set("notes", e.target.value)} rows={3} className="w-full text-sm border border-[#D4C8B8] rounded-lg px-3 py-2 focus:outline-none focus:border-[#B8965A] resize-none" placeholder="Internal notes about this partner" />
              </FormRow>
            </div>
          )}

          {tab === "network" && (
            <div className="space-y-5">
              <FormRow label="Partner Type">
                <div className="flex flex-wrap gap-2 mt-1">
                  {PARTNER_TYPES.map(t => (
                    <button
                      key={t.value}
                      data-testid={`ptype-${t.value}`}
                      onClick={() => set("partner_type", form.partner_type === t.value ? "" : t.value)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        form.partner_type === t.value ? "bg-[#B8965A] text-white border-[#B8965A]" : `${t.color} border-current border-opacity-30 hover:border-[#B8965A]`
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </FormRow>
              <FormRow label="Capability Tags" hint="Comma-separated e.g. LED Stage Lighting, Live Band, Drone Coverage">
                <Input
                  data-testid="field-capability-tags"
                  value={form.capability_tags}
                  onChange={e => set("capability_tags", e.target.value)}
                  placeholder="e.g. LED Stage Lighting, Live Band, Drone Coverage"
                  className="border-[#D4C8B8] focus:border-[#B8965A]"
                />
              </FormRow>
              <div>
                <label className="text-sm font-medium text-[#3D3D3D] block mb-2">Geographic Coverage</label>
                <div className="bg-[#FAFAF8] border border-[#E8E0D0] rounded-xl p-4 space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.geo_pan_india} onChange={e => set("geo_pan_india", e.target.checked)} className="accent-[#B8965A]" data-testid="geo-pan-india-check" />
                    <span className="text-sm text-[#3D3D3D]">Pan-India Coverage</span>
                  </label>
                  <FormRow label="Cities">
                    <Input data-testid="field-geo-cities" value={form.geo_cities} onChange={e => set("geo_cities", e.target.value)} placeholder="e.g. Mumbai, Delhi, Bangalore" className="border-[#D4C8B8] focus:border-[#B8965A] text-sm" />
                  </FormRow>
                  <FormRow label="States">
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {INDIA_STATES.map(s => (
                        <button
                          key={s}
                          onClick={() => set("geo_states", form.geo_states.includes(s) ? form.geo_states.filter(x => x !== s) : [...form.geo_states, s])}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${form.geo_states.includes(s) ? "bg-[#B8965A] text-white border-[#B8965A]" : "bg-white text-[#6B5E4A] border-[#D4C8B8] hover:border-[#B8965A]"}`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </FormRow>
                </div>
              </div>
            </div>
          )}

          {tab === "certs" && (
            <div className="space-y-3">
              {form.certifications.map((c, i) => (
                <div key={i} className="bg-[#FAFAF8] border border-[#E8E0D0] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <select value={c.cert_type} onChange={e => updateCert(i, "cert_type", e.target.value)} className="text-sm border border-[#D4C8B8] rounded px-2 py-1 focus:outline-none focus:border-[#B8965A]">
                      {CERT_TYPES.map(ct => <option key={ct} value={ct}>{ct}</option>)}
                    </select>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={c.verified} onChange={e => updateCert(i, "verified", e.target.checked)} className="accent-[#B8965A]" />
                        <span className="text-xs text-[#6B6B6B]">Verified</span>
                      </label>
                      <button onClick={() => removeCert(i)} className="text-red-500 hover:text-red-700"><X className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input value={c.cert_number || ""} onChange={e => updateCert(i, "cert_number", e.target.value)} placeholder="Cert number" className="border-[#D4C8B8] text-sm" />
                    <Input value={c.issued_by || ""} onChange={e => updateCert(i, "issued_by", e.target.value)} placeholder="Issued by" className="border-[#D4C8B8] text-sm" />
                    <Input type="date" value={c.valid_from || ""} onChange={e => updateCert(i, "valid_from", e.target.value)} className="border-[#D4C8B8] text-sm" />
                    <Input type="date" value={c.valid_until || ""} onChange={e => updateCert(i, "valid_until", e.target.value)} className="border-[#D4C8B8] text-sm" />
                  </div>
                </div>
              ))}
              <button onClick={addCert} className="w-full py-2.5 border-2 border-dashed border-[#D4C8B8] rounded-xl text-sm text-[#9B8B7A] hover:border-[#B8965A] hover:text-[#B8965A] transition-colors">
                + Add Certification
              </button>
            </div>
          )}

          {tab === "crm" && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                <strong>Note:</strong> CRM account linkage is optional. A linked CRM account records a relationship — it does not affect sourcing eligibility. Partner identity and CRM account identity are separate.
              </div>
              {form.crm_account_id ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-900">{form.crm_account_name || form.crm_account_id}</p>
                    <p className="text-xs text-emerald-700 font-mono mt-0.5">{form.crm_account_id}</p>
                  </div>
                  <button onClick={() => { set("crm_account_id", ""); set("crm_account_name", ""); setCrmSearch(""); }} className="text-xs text-red-600 border border-red-200 px-2 py-1 rounded hover:bg-red-50">
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <FormRow label="Search CRM Account">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9B8B7A]" />
                      <Input data-testid="crm-account-search" value={crmSearch} onChange={e => handleCrmInput(e.target.value)} placeholder="Search by company or account name…" className="pl-9 border-[#D4C8B8] focus:border-[#B8965A]" />
                    </div>
                  </FormRow>
                  {crmSearching && <p className="text-xs text-[#9B8B7A] mt-1">Searching…</p>}
                  {crmResults.length > 0 && (
                    <div className="mt-2 border border-[#E8E0D0] rounded-xl overflow-hidden">
                      {crmResults.map((r, i) => (
                        <button
                          key={r._id || r.id || i}
                          data-testid={`crm-result-${i}`}
                          className="w-full text-left px-4 py-3 hover:bg-[#F5EFE5] border-b border-[#F0E8DC] last:border-0"
                          onClick={() => {
                            set("crm_account_id", r._id || r.id || "");
                            set("crm_account_name", r.company_name || r.customer_name || r.name || "");
                            setCrmResults([]);
                            setCrmSearch(r.company_name || r.customer_name || r.name || "");
                          }}
                        >
                          <p className="text-sm font-medium text-[#1C1C1E]">{r.company_name || r.customer_name || r.name}</p>
                          <p className="text-xs text-[#9B8B7A] mt-0.5">{r.email || r._id || r.id}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E8E0D0] flex justify-end gap-3 flex-shrink-0">
          <Button variant="outline" onClick={onClose} className="border-[#D4C8B8] text-[#6B6B6B]">Cancel</Button>
          <Button data-testid="save-partner-btn" onClick={handleSave} disabled={saving} className="bg-[#B8965A] hover:bg-[#A07840] text-white">
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Partner"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Dry-Run Modal ─────────────────────────────────────────────────────────────
function DryRunModal({ result, onClose }) {
  const CONF_STYLES = {
    HIGH:         "bg-emerald-50 text-emerald-800 border-emerald-200",
    AMBIGUOUS:    "bg-amber-50 text-amber-800 border-amber-200",
    UNRESOLVABLE: "bg-red-50 text-red-800 border-red-200",
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="dry-run-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E8E0D0] flex items-center justify-between">
          <div>
            <h2 className="font-serif text-lg font-semibold text-[#1C1C1E]">Assignment Reconciliation Dry-Run</h2>
            <p className="text-xs text-[#9B8B7A] mt-0.5">READ-ONLY — No assignments modified · {result.run_at?.slice(0,10)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-[#F5EFE5]"><X className="h-5 w-5 text-[#6B6B6B]" /></button>
        </div>
        {/* Summary Chips */}
        <div className="px-6 py-3 flex gap-4 border-b border-[#F0E8DC]">
          <DryRunStat label="Total" value={result.total} color="text-[#3D3D3D]" />
          <DryRunStat label="HIGH" value={result.high} color="text-emerald-700" />
          <DryRunStat label="AMBIGUOUS" value={result.ambiguous} color="text-amber-700" />
          <DryRunStat label="UNRESOLVABLE" value={result.unresolvable} color="text-red-700" />
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" data-testid="dry-run-results">
          {(result.results || []).map((r, i) => (
            <div key={i} className={`border rounded-xl p-4 ${CONF_STYLES[r.confidence] || "bg-slate-50"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-sm">{r.partner_name}</p>
                  <p className="text-xs mt-0.5 opacity-70">Assignment: {r.assignment_status} · Event: {r.event_id?.slice(0,16) || "unknown"}</p>
                </div>
                <span className={`text-xs font-semibold px-2 py-1 rounded border ${CONF_STYLES[r.confidence]}`}>{r.confidence}</span>
              </div>
              {r.candidate_company_name && (
                <p className="text-xs mt-2 font-medium">Candidate: {r.candidate_company_name} <span className="opacity-60">({r.candidate_status})</span></p>
              )}
              <p className="text-xs mt-1 opacity-70">{r.reason}</p>
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-[#E8E0D0] bg-amber-50">
          <p className="text-xs text-amber-800"><strong>Next step:</strong> Authorize PARTNER-ASSIGN-02 to apply HIGH confidence matches. Review AMBIGUOUS cases manually.</p>
        </div>
      </div>
    </div>
  );
}

// ── Helper sub-components ──────────────────────────────────────────────────────
function Section({ title, icon, children }) {
  return (
    <div className="px-6 py-4 border-b border-[#F0E8DC]">
      <div className="flex items-center gap-2 mb-3 text-[#6B5E4A]">
        {icon}
        <h4 className="text-xs font-semibold uppercase tracking-wide">{title}</h4>
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 mb-1.5">
      <span className="text-xs text-[#9B8B7A] w-24 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-sm text-[#1C1C1E] flex-1">{value}</span>
    </div>
  );
}

function SourcingCount({ label, value }) {
  return (
    <div className="text-center bg-[#FAFAF8] border border-[#E8E0D0] rounded-lg py-2">
      <p className="text-xl font-bold text-[#B8965A]">{value}</p>
      <p className="text-xs text-[#9B8B7A] mt-0.5">{label}</p>
    </div>
  );
}

function FilterSelect({ label, value, options, onChange, testId }) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="text-sm border border-[#D4C8B8] rounded-lg px-3 py-1.5 bg-white text-[#3D3D3D] focus:outline-none focus:border-[#B8965A]"
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function CityInput({ value, onChange, testId }) {
  return (
    <div className="relative">
      <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9B8B7A]" />
      <input
        data-testid={testId}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Filter by city"
        className="text-sm border border-[#D4C8B8] rounded-lg pl-7 pr-3 py-1.5 bg-white text-[#3D3D3D] focus:outline-none focus:border-[#B8965A] w-32"
      />
    </div>
  );
}

function FormRow({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#3D3D3D] mb-1.5">{label}
        {hint && <span className="font-normal text-xs text-[#9B8B7A] ml-1.5">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function DryRunStat({ label, value, color }) {
  return (
    <div className="text-center">
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-[#9B8B7A]">{label}</p>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="text-center py-20" data-testid="partner-empty-state">
      <div className="w-16 h-16 rounded-2xl bg-[#F5EFE5] flex items-center justify-center mx-auto mb-4">
        <Building2 className="h-8 w-8 text-[#B8965A]" />
      </div>
      <h3 className="text-lg font-semibold text-[#1C1C1E] mb-2">No partners found</h3>
      <p className="text-sm text-[#6B6B6B] mb-5">Start building your partner network by adding your first partner.</p>
      <Button onClick={onAdd} className="bg-[#B8965A] hover:bg-[#A07840] text-white">
        <Plus className="h-4 w-4 mr-1.5" />Add First Partner
      </Button>
    </div>
  );
}
